// Chakra imports
import {
  Badge,
  Box,
  Button,
  Circle,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { CheckIcon, DeleteIcon } from "@chakra-ui/icons";
import React, { useRef, useState } from "react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiCheckCircle,
  FiGlobe,
  FiImage,
  FiMapPin,
  FiPhone,
  FiSettings,
  FiUploadCloud,
} from "react-icons/fi";
import { fileUpload } from "../../Services/FileService";
import { registerSchool } from "./services";

const initialForm = {
  name: "",
  type: "",
  subtype: "",
  description: "",
  address: "",
  email: "",
  phone: "",
  website: "",
  students: "",
  classes: "",
  maxBells: "",
  speakersNeeded: false,
  logo: null,
  logoPreview: null,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;

// Fields validated per step so a user can't advance with missing info.
const steps = [
  {
    title: "Institution",
    subtitle: "Tell us about your school",
    icon: FiBookOpen,
    fields: ["name", "type", "subtype"],
  },
  {
    title: "Contact",
    subtitle: "How can we reach you?",
    icon: FiPhone,
    fields: ["address", "email", "phone", "website"],
  },
  {
    title: "Capacity",
    subtitle: "Bell and enrollment setup",
    icon: FiSettings,
    fields: ["students", "classes", "maxBells"],
  },
  {
    title: "Review",
    subtitle: "Confirm and submit",
    icon: FiCheckCircle,
    fields: [],
  },
];

const requiredFields = [
  "name",
  "type",
  "subtype",
  "address",
  "email",
  "phone",
  "students",
  "classes",
  "maxBells",
];

function SignUp() {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const titleColor = useColorModeValue("teal.300", "teal.200");
  const textColor = useColorModeValue("gray.700", "white");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const bgColor = useColorModeValue("white", "gray.700");
  const sectionBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const inputBg = useColorModeValue("white", "gray.800");
  const softBg = useColorModeValue("teal.50", "whiteAlpha.100");

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const validateField = (name, value) => {
    const val = typeof value === "string" ? value.trim() : value;

    if (requiredFields.includes(name) && !val) {
      return "This field is required.";
    }
    if (name === "email" && val && !emailPattern.test(val)) {
      return "Enter a valid email address.";
    }
    if (name === "phone" && val) {
      const digits = val.replace(/\D/g, "");
      if (digits.length < 7 || digits.length > 15) {
        return "Enter a valid phone number.";
      }
    }
    if (name === "website" && val && !urlPattern.test(val)) {
      return "Enter a valid website URL.";
    }
    if (["students", "classes", "maxBells"].includes(name) && val) {
      const num = Number(val);
      if (!Number.isInteger(num) || num < 1) {
        return "Enter a number greater than 0.";
      }
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name] || errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateStep = (stepIndex) => {
    const stepFields = steps[stepIndex].fields;
    const nextErrors = {};
    stepFields.forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) nextErrors[field] = error;
    });
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    setTouched((prev) =>
      stepFields.reduce((acc, field) => ({ ...acc, [field]: true }), { ...prev })
    );
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(activeStep)) {
      toast({
        title: "Check the highlighted fields",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleLogoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please upload an image file.", status: "warning", duration: 3500, isClosable: true });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Logo must be smaller than 2 MB.", status: "warning", duration: 3500, isClosable: true });
      return;
    }

    setIsUploadingLogo(true);
    try {
      const fileUrl = await fileUpload(file, "school");
      const previewUrl = URL.createObjectURL(file);
      setForm((prev) => {
        if (prev.logoPreview) URL.revokeObjectURL(prev.logoPreview);
        return { ...prev, logo: fileUrl, logoPreview: previewUrl };
      });
      toast({ title: "Logo uploaded", status: "success", duration: 2500, isClosable: true });
    } catch (error) {
      console.error(error);
      toast({ title: "Failed to upload logo.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = () => {
    setForm((prev) => {
      if (prev.logoPreview) URL.revokeObjectURL(prev.logoPreview);
      return { ...prev, logo: null, logoPreview: null };
    });
  };

  const handleSubmit = async () => {
    // Validate everything one last time before hitting the API.
    const allValid = requiredFields.every((field) => !validateField(field, form[field]));
    if (!allValid) {
      setTouched(requiredFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));
      setErrors(
        requiredFields.reduce((acc, f) => {
          const err = validateField(f, form[f]);
          if (err) acc[f] = err;
          return acc;
        }, {})
      );
      setActiveStep(0);
      toast({ title: "Please complete all required fields.", status: "warning", duration: 3500, isClosable: true });
      return;
    }

    const request = {
      name: form.name.trim(),
      address: form.address.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      website: form.website.trim(),
      type: form.type,
      subType: form.subtype,
      totalStudent: Number(form.students),
      totalClass: Number(form.classes),
      speakersNeeded: form.speakersNeeded,
      description: form.description.trim(),
      logo: form.logo,
      maxBell: Number(form.maxBells),
    };

    setIsSubmitting(true);
    try {
      const response = await registerSchool(request);
      if (response.status === 200 || response.status === 201) {
        setIsDone(true);
        toast({
          title: "Registration submitted!",
          description: "Your school has been registered successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({ title: "Something went wrong. Please try again.", status: "error", duration: 4000, isClosable: true });
      }
    } catch (error) {
      console.error("Error registering school:", error);
      toast({ title: "Failed to register school. Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const completedRequired = requiredFields.filter((f) => !validateField(f, form[f])).length;
  const completionPercent = Math.round((completedRequired / requiredFields.length) * 100);

  const fieldProps = {
    bg: inputBg,
    borderColor,
    borderRadius: "12px",
    _hover: { borderColor: "teal.300" },
    _focus: { borderColor: "teal.400", boxShadow: "0 0 0 1px var(--chakra-colors-teal-400)" },
  };

  const renderInput = ({ name, label, placeholder, type = "text", icon, helper }) => (
    <FormControl isRequired={requiredFields.includes(name)} isInvalid={Boolean(touched[name] && errors[name])}>
      <FormLabel fontSize="sm" fontWeight="semibold" ms="2px">{label}</FormLabel>
      <Flex align="center" position="relative">
        {icon && (
          <Icon as={icon} color={mutedText} position="absolute" left="12px" zIndex="1" />
        )}
        <Input
          name={name}
          value={form[name]}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          type={type}
          size="lg"
          fontSize="sm"
          pl={icon ? "40px" : undefined}
          {...fieldProps}
        />
      </Flex>
      {helper && !errors[name] && <FormHelperText fontSize="xs">{helper}</FormHelperText>}
      <FormErrorMessage>{errors[name]}</FormErrorMessage>
    </FormControl>
  );

  const reviewRows = [
    { label: "Institution", value: form.name },
    { label: "Type", value: form.type },
    { label: "Sub Type", value: form.subtype },
    { label: "School Address", value: form.address },
    { label: "Email", value: form.email },
    { label: "Phone", value: form.phone },
    { label: "Website", value: form.website || "—" },
    { label: "Students", value: form.students },
    { label: "Classes", value: form.classes },
    { label: "Maximum Bells", value: form.maxBells },
    { label: "Speakers Needed", value: form.speakersNeeded ? "Yes" : "No" },
  ];

  return (
    <Flex
      direction="column"
      align="center"
      w="100%"
      px={{ base: "16px", md: "24px" }}
      pt={{ base: "28px", md: "40px" }}
      pb="40px">
      <Flex
        direction="column"
        textAlign="center"
        align="center"
        mb="24px"
        maxW="640px"
        w="100%">
        <Text fontSize={{ base: "2xl", md: "3xl" }} color={textColor} fontWeight="bold">
          Register Your School
        </Text>
        <Text
          fontSize="sm"
          color={mutedText}
          fontWeight="normal"
          mt="8px">
          Join Digital Ghanti. Fill in your institution's details to set up your
          digital bell system.
        </Text>
      </Flex>

      <Flex alignItems="flex-start" justifyContent="center" w="100%">
        <Flex
          direction="column"
          w={{ base: "92%", md: "640px" }}
          background="transparent"
          borderRadius="15px"
          p={{ base: "24px", md: "40px" }}
          mx="auto"
          bg={bgColor}
          boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)">

          {isDone ? (
            <Flex direction="column" align="center" textAlign="center" py="30px">
              <Circle size="72px" bg="teal.50" color="teal.400" mb="20px">
                <Icon as={FiCheckCircle} boxSize={9} />
              </Circle>
              <Text fontSize="2xl" fontWeight="bold" color={textColor} mb="8px">
                You're all set!
              </Text>
              <Text color={mutedText} maxW="80%" mb="28px">
                <b>{form.name}</b> has been registered successfully. Our team will
                review your details and get in touch shortly.
              </Text>
              <Button
                bg="teal.300"
                color="white"
                _hover={{ bg: "teal.200" }}
                onClick={() => (window.location.href = "/#/auth/signin")}>
                Go to Sign In
              </Button>
            </Flex>
          ) : (
            <>
              {/* Stepper header */}
              <Flex justify="space-between" align="flex-start" mb="6px">
                {steps.map((step, index) => {
                  const isActive = index === activeStep;
                  const isComplete = index < activeStep;
                  return (
                    <Flex key={step.title} direction="column" align="center" flex="1" position="relative">
                      {index > 0 && (
                        <Box
                          position="absolute"
                          top="18px"
                          left="-50%"
                          w="100%"
                          h="2px"
                          bg={index <= activeStep ? "teal.300" : borderColor}
                          zIndex="0"
                        />
                      )}
                      <Circle
                        size="38px"
                        bg={isActive || isComplete ? "teal.300" : sectionBg}
                        color={isActive || isComplete ? "white" : mutedText}
                        borderWidth={isActive ? "0" : "1px"}
                        borderColor={borderColor}
                        zIndex="1"
                        transition="all .2s ease">
                        <Icon as={isComplete ? CheckIcon : step.icon} boxSize={isComplete ? 3.5 : 4} />
                      </Circle>
                      <Text
                        fontSize="xs"
                        fontWeight={isActive ? "bold" : "medium"}
                        color={isActive ? titleColor : mutedText}
                        mt="6px"
                        display={{ base: "none", sm: "block" }}>
                        {step.title}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>

              <Box mb="20px" mt="16px">
                <Flex justify="space-between" fontSize="xs" color={mutedText} mb="6px">
                  <Text>{steps[activeStep].subtitle}</Text>
                  <Text fontWeight="bold">{completionPercent}% complete</Text>
                </Flex>
                <Progress value={completionPercent} size="sm" borderRadius="full" colorScheme="teal" />
              </Box>

              {/* Step 1 - Institution */}
              {activeStep === 0 && (
                <Stack spacing="18px">
                  {renderInput({
                    name: "name",
                    label: "Name of Institution",
                    placeholder: "e.g. Shree Saraswati School",
                    icon: FiBookOpen,
                  })}
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="18px">
                    <FormControl isRequired isInvalid={Boolean(touched.type && errors.type)}>
                      <FormLabel fontSize="sm" fontWeight="semibold" ms="2px">Organization Type</FormLabel>
                      <Select
                        name="type"
                        placeholder="Select type"
                        value={form.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="lg"
                        fontSize="sm"
                        {...fieldProps}>
                        <option value="School">School</option>
                        <option value="College">College</option>
                        <option value="Office">Office</option>
                        <option value="Others">Others</option>
                      </Select>
                      <FormErrorMessage>{errors.type}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={Boolean(touched.subtype && errors.subtype)}>
                      <FormLabel fontSize="sm" fontWeight="semibold" ms="2px">Sub Type</FormLabel>
                      <Select
                        name="subtype"
                        placeholder="Select sub type"
                        value={form.subtype}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="lg"
                        fontSize="sm"
                        {...fieldProps}>
                        <option value="PRIVATE">Private</option>
                        <option value="PUBLIC">Public</option>
                        <option value="COMMUNITY">Community</option>
                        <option value="OTHERS">Others</option>
                      </Select>
                      <FormErrorMessage>{errors.subtype}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  {/* Logo uploader */}
                  <Box
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderColor={form.logoPreview ? "teal.300" : borderColor}
                    borderRadius="14px"
                    bg={form.logoPreview ? softBg : inputBg}
                    p="16px">
                    <Flex gap="16px" align="center" direction={{ base: "column", sm: "row" }}>
                      <Flex
                        w="80px"
                        h="80px"
                        align="center"
                        justify="center"
                        borderRadius="14px"
                        bg={inputBg}
                        borderWidth="1px"
                        borderColor={borderColor}
                        overflow="hidden">
                        {form.logoPreview ? (
                          <Image src={form.logoPreview} alt="Logo preview" w="100%" h="100%" objectFit="cover" />
                        ) : (
                          <Icon as={FiImage} color="teal.400" boxSize={7} />
                        )}
                      </Flex>
                      <Box flex="1">
                        <Text fontSize="sm" fontWeight="semibold">
                          {form.logoPreview ? "Logo uploaded" : "School logo (optional)"}
                        </Text>
                        <Text fontSize="xs" color={mutedText} mt="2px">PNG, JPG or WEBP. Max 2 MB.</Text>
                        <HStack mt="10px" spacing="8px">
                          <Button
                            size="sm"
                            leftIcon={form.logoPreview ? <CheckIcon /> : <FiUploadCloud />}
                            colorScheme="teal"
                            variant={form.logoPreview ? "outline" : "solid"}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            isLoading={isUploadingLogo}
                            loadingText="Uploading">
                            {form.logoPreview ? "Replace" : "Upload"}
                          </Button>
                          {form.logoPreview && (
                            <IconButton
                              size="sm"
                              icon={<DeleteIcon />}
                              aria-label="Remove logo"
                              variant="ghost"
                              colorScheme="red"
                              onClick={removeLogo}
                            />
                          )}
                        </HStack>
                        <VisuallyHidden>
                          <Input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoChange} />
                        </VisuallyHidden>
                      </Box>
                    </Flex>
                  </Box>

                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="semibold" ms="2px">Description</FormLabel>
                    <Textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="A short note about your institution (optional)"
                      minH="90px"
                      resize="vertical"
                      fontSize="sm"
                      {...fieldProps}
                    />
                  </FormControl>
                </Stack>
              )}

              {/* Step 2 - Contact */}
              {activeStep === 1 && (
                <Stack spacing="18px">
                  {renderInput({
                    name: "address",
                    label: "School Address",
                    placeholder: "Enter school's full address",
                    icon: FiMapPin,
                    helper: "Where your institution is located.",
                  })}
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="18px">
                    {renderInput({
                      name: "email",
                      label: "Email",
                      placeholder: "school@example.com",
                      type: "email",
                      icon: FiGlobe,
                    })}
                    {renderInput({
                      name: "phone",
                      label: "Contact Number",
                      placeholder: "Enter contact number",
                      type: "tel",
                      icon: FiPhone,
                    })}
                  </SimpleGrid>
                  {renderInput({
                    name: "website",
                    label: "Website",
                    placeholder: "https://yourschool.edu",
                    type: "url",
                    icon: FiGlobe,
                    helper: "Optional",
                  })}
                </Stack>
              )}

              {/* Step 3 - Capacity */}
              {activeStep === 2 && (
                <Stack spacing="18px">
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="18px">
                    {renderInput({ name: "students", label: "Total Students", placeholder: "e.g. 500", type: "number" })}
                    {renderInput({ name: "classes", label: "Total Classes", placeholder: "e.g. 20", type: "number" })}
                    {renderInput({ name: "maxBells", label: "Maximum Bells", placeholder: "e.g. 40", type: "number" })}
                  </SimpleGrid>
                  <Flex
                    align="center"
                    justify="space-between"
                    gap="16px"
                    p="16px"
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="12px"
                    bg={sectionBg}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">Speakers Needed</Text>
                      <Text fontSize="xs" color={mutedText}>Turn on if installation requires speaker support.</Text>
                    </Box>
                    <Switch
                      colorScheme="teal"
                      isChecked={form.speakersNeeded}
                      onChange={(e) => setForm((prev) => ({ ...prev, speakersNeeded: e.target.checked }))}
                    />
                  </Flex>
                </Stack>
              )}

              {/* Step 4 - Review */}
              {activeStep === 3 && (
                <Stack spacing="14px">
                  <HStack>
                    <Badge colorScheme="teal" borderRadius="full" px="3">Almost there</Badge>
                    <Text fontSize="sm" color={mutedText}>Please review your details before submitting.</Text>
                  </HStack>
                  <Box borderWidth="1px" borderColor={borderColor} borderRadius="14px" overflow="hidden">
                    {reviewRows.map((row, idx) => (
                      <Flex
                        key={row.label}
                        justify="space-between"
                        align="center"
                        px="16px"
                        py="10px"
                        bg={idx % 2 === 0 ? sectionBg : "transparent"}
                        gap="12px">
                        <Text fontSize="sm" color={mutedText}>{row.label}</Text>
                        <Text fontSize="sm" fontWeight="semibold" textAlign="right" noOfLines={1}>
                          {row.value}
                        </Text>
                      </Flex>
                    ))}
                  </Box>
                </Stack>
              )}

              {/* Navigation */}
              <Flex mt="28px" justify="space-between" align="center" gap="12px">
                <Button
                  variant="ghost"
                  leftIcon={<FiArrowLeft />}
                  onClick={goBack}
                  isDisabled={activeStep === 0 || isSubmitting}>
                  Back
                </Button>

                {activeStep < steps.length - 1 ? (
                  <Button
                    bg="teal.300"
                    color="white"
                    rightIcon={<FiArrowRight />}
                    _hover={{ bg: "teal.200" }}
                    _active={{ bg: "teal.400" }}
                    onClick={goNext}>
                    Continue
                  </Button>
                ) : (
                  <Button
                    bg="teal.300"
                    color="white"
                    rightIcon={<FiCheckCircle />}
                    _hover={{ bg: "teal.200" }}
                    _active={{ bg: "teal.400" }}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    loadingText="Submitting"
                    isDisabled={isUploadingLogo}>
                    Register School
                  </Button>
                )}
              </Flex>

              <Flex justify="center" mt="20px">
                <Text color={textColor} fontWeight="medium" fontSize="sm">
                  Already have an account?
                  <Text
                    as="span"
                    color={titleColor}
                    ms="5px"
                    fontWeight="bold"
                    cursor="pointer"
                    onClick={() => (window.location.href = "/#/auth/signin")}>
                    Sign In
                  </Text>
                </Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

export default SignUp;
